import React from 'react';
import { Box, Divider, Switch, Typography } from '@mui/material';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { InlineMessage, SectionCard } from '../ui';
import type { IntegrationSettings, LightspeedEcomPushSettings } from '../../services/integrationApi';

const T = 'integrations.lightspeedEcom.settings';

export const LIGHTSPEED_ECOM_PUSH_KEYS: Array<keyof LightspeedEcomPushSettings> = ['content', 'description', 'tags', 'metafields', 'visibility'];

/** What a missing key means; mirrors `LIGHTSPEED_ECOM_PUSH_DEFAULTS` on the API. */
export const LIGHTSPEED_ECOM_PUSH_DEFAULTS: Required<LightspeedEcomPushSettings> = {
  content: true,
  description: true,
  tags: true,
  metafields: true,
  visibility: false,
};

/** The content fields a Lightspeed eCom integration writes, in display order (what its card lists). */
export function enabledLightspeedEcomFields(settings: IntegrationSettings | undefined | null): Array<keyof LightspeedEcomPushSettings> {
  const current = settings?.lightspeedEcom || {};
  return LIGHTSPEED_ECOM_PUSH_KEYS.filter((key) => current[key] ?? LIGHTSPEED_ECOM_PUSH_DEFAULTS[key]);
}

interface Props {
  settings: IntegrationSettings;
  onChange: (partial: Partial<IntegrationSettings>) => void;
}

/**
 * What the Lightspeed eCom (C-Series) sync writes: the content fields, each a switch, under a
 * note that price, stock, variants, titles, brand, categories, article codes, barcodes and images
 * stay with Lightspeed Retail, which feeds the web store through Lightspeed's own connector.
 */
export const LightspeedEcomSettingsSection: React.FC<Props> = ({ settings, onChange }) => {
  const { t } = useAppTranslation();
  const current = settings.lightspeedEcom || {};
  const valueOf = (key: keyof LightspeedEcomPushSettings) => current[key] ?? LIGHTSPEED_ECOM_PUSH_DEFAULTS[key];
  const set = (key: keyof LightspeedEcomPushSettings, value: boolean) =>
    onChange({ lightspeedEcom: { ...current, [key]: value } });

  return (
    <SectionCard title={t(`${T}.title`)} subtitle={t(`${T}.subtitle`)} dense padded>
      <InlineMessage severity="info">{t(`${T}.retailOwns`)}</InlineMessage>
      <Box>
        {LIGHTSPEED_ECOM_PUSH_KEYS.map((key, index) => (
          <React.Fragment key={key}>
            {index > 0 && <Divider />}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1.25, gap: 1.5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t(`${T}.fields.${key}.label`)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
                  {t(`${T}.fields.${key}.desc`)}
                </Typography>
              </Box>
              <Switch
                size="small"
                checked={valueOf(key)}
                onChange={(e) => set(key, e.target.checked)}
                inputProps={{ 'aria-label': t(`${T}.fields.${key}.label`) }}
              />
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </SectionCard>
  );
};

export default LightspeedEcomSettingsSection;
